/**
 * L'historique des versions — LA SOURCE DE VÉRITÉ.
 *
 * `APP_VERSION` en découle (voir version.ts), donc la version affichée dans la barre latérale et
 * renvoyée par /health ne peut pas diverger de ce qui est écrit ici : publier une version, c'est
 * ajouter une entrée en tête. C'est ce qui manquait — la version était une constante isolée que
 * personne n'avait de raison de toucher, et elle est restée à 1.0.0 pendant tout le mois d'août.
 *
 * ÉCRIT POUR LES RELECTEURS, PAS POUR LES DÉVELOPPEURS. Chaque ligne dit ce qui change à l'écran ou
 * dans le travail, jamais le nom d'un fichier ni celui d'une colonne — sauf quand la colonne EST le
 * sujet (le relecteur la voit dans la fiche). Les messages de commit ne sont pas recopiés : ils
 * parlent au dépôt, pas à l'équipe.
 *
 * EN FRANÇAIS SEULEMENT, contrairement au reste de la page Documentation. L'application s'ouvre en
 * français depuis le 28-08, l'équipe est francophone, et un journal traduit dérive : la version
 * anglaise cesse d'être mise à jour et finit par mentir. Une seule langue tenue à jour vaut mieux que
 * trois dont deux sont fausses.
 *
 * Numérotation : chaque LOT DÉPLOYÉ est une version mineure (1.x.0), les correctifs qui suivent un
 * déploiement dans la même journée sont des correctifs (1.x.y). Les dates sont celles du déploiement
 * réel en production, pas celles des commits.
 */
/** La plus récente EN PREMIER — `APP_VERSION` lit CHANGELOG[0].version. */
export const CHANGELOG = [
    {
        version: '1.31.0',
        date: '2026-11-02',
        title: 'Releaser la boîte d’un immeuble restée en SDU',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Une adresse marquée SDU dans le DMP, alors qu’une autre unité de la même adresse est déjà releasée, est traitée comme la boîte d’immeuble qu’elle est. « Déduire du bâtiment » reprend son PoP, la release ne demande plus de distance depuis la rue, et elle passe l’adresse en MDU. Une vraie maison (SDU) ne change pas.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Sur une adresse dans la build list, le statut de livraison ne se modifie plus que par un admin : pour un validateur, la correction du code est remplacée par la date de la build list, et la release garde le code déjà enregistré.',
            },
            {
                kind: 'fix', area: 'Fenêtre de validation',
                text: 'La pastille « Source : snapshot ticket » (ou « DMP en direct ») a quitté l’en-tête de l’adresse : elle recouvrait le bonhomme Street View, de nouveau cliquable.',
            },
            {
                kind: 'fix', area: 'Console DMP',
                text: 'La blacklist de Proximus se recharge de nouveau. Depuis que Proximus a renommé son fichier, fin octobre, l’app gardait l’ancienne liste ; elle prend désormais le fichier le plus récent du dossier, quel que soit son nom.',
            },
        ],
    },
    {
        version: '1.30.0',
        date: '2026-10-22',
        title: 'Voir d’un coup d’œil si une adresse est commercialisée',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Les pastilles « Service file » et « Deployment list » de la console DMP sont maintenant dans la fenêtre de validation, à côté de SSV / TSA, pour tous les validateurs. Sur un immeuble, chaque adresse a les siennes, avec un compte (« Service file 2/3 ») : vert si tous les logements sont dans le fichier, ambre si une partie, gris si aucun. Un clic montre la ligne du fichier, logement par logement.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation et console DMP',
                text: 'Sur une adresse connectée (HOMES_CONNECT), l’UTAC s’affiche : à côté de l’avertissement « client connecté », dans la fiche de l’adresse, et sous le statut du logement dans le tableau d’un immeuble.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Les motifs saisis lors d’une correction (colonne corrigée, statut corrigé, retour blacklist forcé, ticket délié) s’affichent dans un bloc « Commentaires », sous la fiche de l’adresse. Le bloc n’apparaît que si l’adresse en a.',
            },
        ],
    },
    {
        version: '1.29.0',
        date: '2026-10-21',
        title: 'Un ticket, plusieurs adresses',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Quand l’opérateur demande plusieurs adresses dans un même ticket, « Ajouter une adresse » les rattache au ticket, par rue et numéro ou par JVID. Chaque adresse a son onglet, sa fiche et son verdict. Une adresse déjà dans un autre ticket ouvert est refusée, avec le numéro de ce ticket. Une adresse ajoutée se retire par la croix de son onglet, après confirmation.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Sur un ticket à plusieurs adresses, la question à l’opérateur, l’envoi vers Build et l’escalade valent pour tout le ticket, depuis la carte « Le ticket entier ». Tant qu’une réponse est attendue, les verdicts sont grisés.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Sur un ticket à plusieurs adresses, un verdict n’écrit plus rien dans Uniticket. Quand chaque adresse a le sien, « Résoudre le ticket » prépare un récapitulatif en anglais (une ligne par adresse, avec la note ou le motif du rejet), qu’on relit et modifie, puis le poste en un seul commentaire et passe le ticket en « Résolu ». Si Proximus répond ensuite, le ticket revient dans la file, ses verdicts intacts.',
            },
            {
                kind: 'feature', area: 'File de validation et statistiques',
                text: 'Un ticket à plusieurs adresses tient sur une seule ligne, avec un badge « 3 adresses · 1/3 », et reste « En attente » jusqu’à « Résoudre ». Chercher n’importe lequel de ses JVID le retrouve. Dans « Traités », il compte une fois.',
            },
            {
                kind: 'fix', area: 'Fenêtre de validation',
                text: 'La ligne du logement du ticket n’est plus rose : elle prend la couleur des autres logements, jaune si l’adresse est inactive. Un logement NOT_AVAILABLE se voit de nouveau.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Rien ne change pour un ticket à une adresse.',
            },
        ],
    },
    {
        version: '1.28.0',
        date: '2026-10-19',
        title: 'Street View s’ouvre dans l’app, au bon endroit',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation et console DMP',
                text: 'Street View s’ouvre dans l’app : le bonhomme, à côté d’« Actions » dans la console et dans l’en-tête de « Référence adresse », montre la façade dans un panneau qui flotte au-dessus de la page, sans quitter le ticket. On le déplace par son en-tête, on l’agrandit par son coin ; la croix ou Échap le ferment sans fermer le tiroir. Le bouton Street View de la fiche d’un logement et l’entrée du menu « Actions » ouvrent le même panneau. Il ne s’affiche que depuis le réseau du bureau, pour les comptes internes ; ailleurs, le lien « Google Maps » de son en-tête montre la même vue. Sans coordonnées, le bonhomme est grisé.',
            },
            {
                kind: 'fix', area: 'Fenêtre de validation et console DMP',
                text: 'Street View, l’épingle et la mesure partent des coordonnées de l’adresse, converties au mètre près. Sur les adresses créées par l’app, ils tombaient jusqu’ici à environ 800 m à côté.',
            },
        ],
    },
    {
        version: '1.27.0',
        date: '2026-10-15',
        title: 'Un PoP manquant se répare depuis la fiche du logement',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Sur un MDU, un logement sans PoP — le plus souvent une unité jamais passée en build list — ne bloque plus le Release : dans la fiche du logement, la ligne rouge « Central POP ID », « Area POP ID » ou « Area ID » propose « Déduire du bâtiment ». La valeur est reprise d’un autre logement du même bâtiment, et le message dit lequel. Sur un SDU, rien n’est proposé : un PoP absent y signale le plus souvent une adresse hors scope, à escalader.',
            },
            {
                kind: 'fix', area: 'Fenêtre de validation',
                text: 'Quand le Release est refusé faute de PoP ou de zone, le message est en français et dit quoi faire, au lieu de renvoyer vers le DMP en anglais.',
            },
            {
                kind: 'fix', area: 'Fenêtre de validation et console DMP',
                text: 'Une boîte ajoutée reprend le PoP d’un logement du bâtiment qui en a un, même quand le logement de départ n’en a pas : elle ne naît plus sans PoP.',
            },
            {
                kind: 'fix', area: 'Console DMP',
                text: '« Déduire du bâtiment » dit en français d’où vient la valeur : « frère du même bâtiment » ou « voisin le plus proche ».',
            },
        ],
    },
    {
        version: '1.26.0',
        date: '2026-10-14',
        title: 'Voir tout ce qui touche un logement, et ne créer que ce que Build a confirmé',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: '« Build a-t-il confirmé ? » remplace « Build doit-il avoir cette adresse ? ». « Oui » crée l’adresse ou les boîtes, qui partiront au PST bis. « Non » ne crée rien et prépare le refus : verdict « Rejeté » et motif pré-remplis sur un ticket lié, message prêt dans « Poser une question » sur un ticket sans JVID. Rien ne part sans relecture, et aucune des deux réponses n’est proposée par défaut.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: '« Build a répondu » se pose dès qu’une personne de Build commente le ticket dans Uniticket, même si le ticket n’a jamais été envoyé chez Build depuis l’application, et dès l’ouverture du ticket, sans attendre la synchronisation. C’est le premier commentaire qui compte.',
            },
            {
                kind: 'feature', area: 'PST bis',
                text: 'Une adresse ou une boîte créée sur un « Oui » est marquée dans le DMP dès sa création : elle entre dans la build list et dans la deployment list à la date du jour, avec la marque du PST bis.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Un caddie rouge signale, dans le tableau des logements, un logement pas encore Homes Connect sur lequel un ordre est en cours. L’infobulle détaille chaque ordre : type, statut, opérateur, référence et date. Pour un SDU, le caddie est dans l’en-tête.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Les logements de la même adresse postale rangés sous un autre building ID, ou sans building ID, apparaissent sous l’adresse, dans un groupe ambre, avec un bandeau : erreur de données probable, à signaler. La capacité affichée ne les compte pas.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Le tableau des logements montre l’adresse déclarée par l’opérateur quand elle diffère de celle du DMP : rue, numéro, extension et boîte, avec la flèche « DMP → déclaré ».',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Deux logements déclarés à la même adresse portent une pastille « doublon », et le formulaire de mapping prévient pendant la saisie. L’enregistrement reste possible.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Un geste qui remet le dossier en attente rouvre le ticket s’il est résolu ou fermé dans Uniticket, et le dit : question à l’opérateur, escalade et retour d’escalade, envoi chez Build, reclassement, verdict « en attente ». Si Uniticket refuse, l’écran le dit aussi, car le dossier ne reviendrait dans aucune vue.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Les zones de message s’ouvrent sur 6 lignes. Après un verdict, une escalade ou un envoi chez Build, le message dit que le ticket suivant est ouvert, avec un bouton pour revenir au ticket quitté pendant 15 secondes.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: '« Ajouter une boîte » montre la capacité et la vérifie comme la fenêtre de validation. Quand elle est nulle, un administrateur doit donner un motif, partout : console, fenêtre de validation et requêtes MDU. Sur un Dx, il précise façade ou arrière.',
            },
            {
                kind: 'feature', area: 'Création d’adresse',
                text: 'Un bandeau ambre prévient quand on crée un logement d’immeuble là où rien n’est construit. Il ne bloque pas.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'La table de mapping d’un JVID se lit dans la fiche, une ligne par opérateur. Les administrateurs peuvent la modifier ou créer une déclaration sans passer par un ticket ; chaque écriture est tracée.',
            },
            {
                kind: 'feature', area: 'Journal d’actions',
                text: 'Chaque ligne dit sur quelle adresse porte l’action (rue, numéro, boîte, JVID), et l’identifiant de l’entrée se copie. Les filtres et la recherche portent sur tout le journal, pas seulement sur ce qui est chargé.',
            },
            {
                kind: 'feature', area: 'Documentation',
                text: 'Une carte du Tutoriel peut être passée d’office quand le code de livraison désigne une seule de ses réponses ; elle reste dans le fil. La case se coche carte par carte, dans l’éditeur.',
            },
            {
                kind: 'data', area: 'Documentation',
                text: 'Les cartes PST bis du Tutoriel suivent la nouvelle question, et une carte « Refuser le ticket : Build n’a pas confirmé » s’ajoute. Une carte corrigée à l’écran n’est pas touchée.',
            },
        ],
    },
    {
        version: '1.25.0',
        date: '2026-09-30',
        title: 'Chaque validateur peut défaire une liaison, et la file ne montre plus de doublon',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: '« Délier ce JVID » est ouvert à tous les validateurs, plus seulement aux administrateurs. Le motif reste obligatoire, et la fenêtre refuse toujours quand le geste n’est plus sûr.',
            },
            {
                kind: 'fix', area: 'Validation d’adresse',
                text: 'Un ticket délié n’apparaît plus deux fois dans la file : la ligne que la liaison erronée avait créée est supprimée, au lieu de rester marquée « Liée par erreur ». Après « Remplacer », l’ancienne ligne disparaît aussi, sauf si un autre ticket la porte encore.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'L’Historique des validations raconte aussi la vie du ticket : liaison, déliaison avec son motif, remplacement d’un JVID par un autre, question à l’opérateur ou note interne, envoi chez Build, escalade et retour, reclassement, pièce jointe, « Build a répondu ». Il s’affiche même quand le ticket n’a pas, ou plus, de JVID.',
            },
            {
                kind: 'data', area: 'Validation d’adresse',
                text: 'Les lignes restées en double dans la file après un « Délier » ou un « Remplacer » sont supprimées.',
            },
            {
                kind: 'data', area: 'Documentation',
                text: 'La carte « Relier le ticket au bon JVID » du Tutoriel ne dit plus qu’il faut un administrateur pour défaire une liaison.',
            },
        ],
    },
    {
        version: '1.24.0',
        date: '2026-09-29',
        title: 'Un tutoriel qui guide carte par carte, et le PST bis décidé au cas par cas',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: '« Build doit-il avoir cette adresse ? » Quand Build a répondu sur le ticket, créer une adresse ou ajouter des boîtes pose la question, après les confirmations habituelles (capacité, voisins). « Non » est la réponse par défaut ; « Revenir au formulaire » n’écrit rien. Depuis la Console DMP, la question est toujours posée.',
            },
            {
                kind: 'feature', area: 'PST bis',
                text: 'Plus aucun envoi d’office : seules les adresses pour lesquelles on a répondu « Oui » partent au PST bis. Les adresses créées avant cette version et encore en attente ne partent plus.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Un marqueur « Build a répondu ». Il se pose tout seul à la première réponse de Build et ne s’efface plus. Il est déjà posé sur les tickets dont la réponse de Build était encore connue. Quand Build a répondu en dehors de l’application, un administrateur le pose à la main, avec un mot d’explication.',
            },
            {
                kind: 'feature', area: 'Registre JVID PST',
                text: 'Une colonne « PST bis » donne la décision prise pour chaque adresse, par qui et quand. Une adresse non envoyée dit pourquoi, et l’export suit.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: '« Délier ce JVID », pour les administrateurs : défaire une liaison faite par erreur, avec un motif obligatoire. Le ticket repasse sans adresse, et sa ligne dans la file est marquée « Liée par erreur ». Rien n’est écrit dans le DMP. Si le geste n’est plus sûr, par exemple parce que l’adresse a été modifiée depuis, la fenêtre refuse et dit pourquoi.',
            },
            {
                kind: 'feature', area: 'Documentation',
                text: 'Le Tutoriel devient un parcours guidé : une carte à la fois, les six phases d’un ticket sur le côté, et « Tout voir » pour sauter à n’importe quelle étape. Un clic sur une réponse précédente y ramène.',
            },
            {
                kind: 'feature', area: 'Documentation',
                text: 'Le Tutoriel fait une partie de la lecture à votre place. Il décode le code de livraison qu’on tape, déduit le scope des listes cochées et compare la distance aux seuils. Les règles de capacité s’ouvrent d’un clic, et « Je suis bloqué » reste la seconde entrée.',
            },
            {
                kind: 'feature', area: 'Documentation',
                text: 'Les administrateurs corrigent chaque carte à l’écran, et un panneau « Contrôle » signale les étapes sans issue ou impossibles à atteindre. L’ancien arbre est archivé tel quel, avec les corrections faites à l’écran.',
            },
        ],
    },
    {
        version: '1.23.0',
        date: '2026-09-25',
        title: 'Des fichiers Build à jour chaque matin, sans dépôt à la main',
        items: [
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Le service file, la deployment list et la blacklist de Proximus sont repris chaque matin à 6 h sur SharePoint, où ils arrivent chaque soir. Plus besoin de les déposer à la main ; le dépôt reste possible, en secours.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Pour chaque fichier, la carte « Fichiers de référence Build » dit quand il a été produit, quand il a été chargé et d’où il vient. « Actualiser » va voir tout de suite ; un service file ou une deployment list de plus de deux jours s’affiche en orange.',
            },
            {
                kind: 'fix', area: 'Fenêtre de validation',
                text: 'Le bloc « Visible chez le partenaire » compare une adresse absente à la date du service file lui-même, et non plus à la date de son dépôt.',
            },
        ],
    },
    {
        version: '1.22.0',
        date: '2026-09-25',
        title: 'Voir les voisins d’une adresse sans refaire une recherche',
        items: [
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Une action « Voisin » dans le menu Actions : les adresses du même côté de la rue s’affichent dans la page, sous le menu — les numéros impairs pour une adresse impaire, les pairs pour une adresse paire. Rien à choisir.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Une carte par numéro : JVID, statut, statut de livraison, type de logement, visible ou non chez le partenaire — le même verdict que la fiche — et en scope ou non. Un immeuble est résumé sur sa carte : nombre de logements, statuts comptés, « 2 / 3 visibles ».',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Les deux numéros existants les plus proches de chaque côté, jusqu’à dix numéros d’écart ; un numéro absent du DMP est signalé entre deux cartes. Cliquer une carte ouvre l’adresse, et la série se recentre sur elle.',
            },
        ],
    },
    {
        version: '1.21.0',
        date: '2026-09-25',
        title: 'Le tutoriel pose la question des bâtiments groupés',
        items: [
            {
                kind: 'feature', area: 'Documentation',
                text: 'Dans le Tutoriel, une nouvelle branche à l’étape « Une adresse postale, jamais un bâtiment » : quand le bâtiment fait partie d’un groupe (plusieurs bâtiments, une même façade). Sa règle de capacité reste à définir, et le tutoriel le dit.',
            },
        ],
    },
    {
        version: '1.20.0',
        date: '2026-09-23',
        title: 'Un tutoriel pour se remettre dans un ticket en quelques minutes',
        items: [
            {
                kind: 'feature', area: 'Documentation',
                text: 'Le Tutoriel déroule tout le parcours d’un ticket, de la lecture de la demande jusqu’au release, avec une seconde entrée : « Je suis bloqué sur un point précis ».',
            },
            {
                kind: 'feature', area: 'Documentation',
                text: 'Une vue en accordéon : on ne déplie que ce qui sert, et on replie tout d’un clic.',
            },
            {
                kind: 'feature', area: 'Documentation',
                text: 'Là où aucune règle n’existe encore, le tutoriel le dit (« à définir en réunion ») au lieu d’en inventer une.',
            },
        ],
    },
    {
        version: '1.19.0',
        date: '2026-09-22',
        title: 'Une adresse archivée se dit archivée, et un ticket mal relié se répare',
        items: [
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Une adresse archivée dans le DMP s’affiche grisée, « archivée le … », au lieu de « introuvable », dans la console comme dans la fenêtre de validation. Elle se lit, on n’y écrit pas.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Quand le JVID du ticket ne désigne plus aucune adresse, l’écran le dit et propose de relier la bonne. La confirmation montre l’ancien et le nouveau JVID côte à côte, et le geste est tracé au journal.',
            },
            {
                kind: 'feature', area: 'Validation d’adresse',
                text: 'La ligne de file de l’ancien JVID n’est pas supprimée : elle est marquée « Remplacée → », avec le nouveau.',
            },
            {
                kind: 'feature', area: 'Documentation',
                text: 'Un nouvel onglet « Tutoriel » : l’arbre de décision pour traiter un ticket, que les administrateurs corrigent directement à l’écran.',
            },
        ],
    },
    {
        version: '1.18.0',
        date: '2026-09-22',
        title: 'Une capacité qui suit enfin les règles du 9 septembre',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Sur poteau : quand un client est déjà connecté sur l’adresse, un seul ajout est possible au total ; sinon, la demande part chez Build.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'En façade avant ou arrière : le relecteur déclare où se trouve l’unité. En façade, la règle du boîtier s’applique ; à l’arrière, la demande part chez Build.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'En façade : la place restante est celle que COCON voit réellement dans le boîtier, voisins compris. « Actualiser » relit aussi COCON.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'La pastille SSV / TSA à côté de la pastille Capacité, quand on travaille un ticket.',
            },
            {
                kind: 'feature', area: 'Création d’adresse',
                text: 'Avec « Release immédiatement » coché, un avertissement si les voisins les plus proches de même parité n’ont aucun logement released : publier quand même, créer sans publier, ou revenir.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Les administrateurs peuvent corriger n’importe quelle colonne d’une adresse.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'La recherche par JVID et la recherche par adresse s’excluent : lancer l’une vide l’autre, et la fiche déjà chargée.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: '« Forcer » une ligne dans le fichier de retour blacklist, depuis la pastille rouge, avec le commentaire « request from proximus ».',
            },
        ],
    },
    {
        version: '1.17.1',
        date: '2026-09-16',
        title: 'Le premier vrai lot de renumérotation passe',
        items: [
            {
                kind: 'fix', area: 'Renumbering',
                text: 'Le vrai lot d’Anouk était refusé : les colonnes « original » et « corrected » sont désormais reconnues.',
            },
            {
                kind: 'fix', area: 'Renumbering',
                text: 'Une boîte corrigée laissée vide était lue « inchangée », et « Combattants 9 » se résolvait sur le 99. Les deux sont corrigés.',
            },
        ],
    },
    {
        version: '1.17.0',
        date: '2026-09-16',
        title: 'Appliquer une renumérotation, simulée avant d’écrire',
        items: [
            {
                kind: 'feature', area: 'Renumbering',
                text: 'Un écran pour les renumérotations d’adresses, réservé aux administrateurs : déposer le fichier, simuler (c’est obligatoire), puis appliquer. Seule la mapping table est modifiée. Un export est prêt pour Proximus.',
            },
        ],
    },
    {
        version: '1.16.0',
        date: '2026-09-15',
        title: 'Voir le boîtier de façade tel que COCON le dessine',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Dans le détail de la capacité, « En savoir plus » ouvre le boîtier de façade tel que COCON le voit : les fibres déjà prises, les nôtres comme celles des voisins, et les places libres.',
            },
        ],
    },
    {
        version: '1.15.0',
        date: '2026-09-15',
        title: 'Ne plus perdre un message vers Build, et commenter une demande MDU',
        items: [
            {
                kind: 'fix', area: 'Fenêtre de validation',
                text: 'Un deuxième message vers Build partait dans le vide, sans erreur : seul le premier arrivait. Chaque envoi part désormais.',
            },
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'Un fil de commentaires internes sur chaque demande, daté et signé, juste au-dessus du bouton pour passer la main.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Un numéro et son extension qui ne commencent pas par le même chiffre s’affichent en rouge, dans la fenêtre, le volet et la console DMP.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Avant d’échanger deux numéros de boîte, un avertissement si ces deux mêmes adresses ont déjà été échangées entre elles.',
            },
            {
                kind: 'feature', area: 'Notes perso',
                text: 'L’onglet s’ouvre sur « Mes notes privées » plutôt que sur les réponses partagées.',
            },
            {
                kind: 'fix', area: 'Langue',
                text: 'L’application s’ouvrait encore en anglais pour certains comptes, malgré le français par défaut : tous les comptes sont passés en français.',
            },
        ],
    },
    {
        version: '1.14.0',
        date: '2026-09-14',
        title: 'Voir le SSV et le TSA d’une adresse sans quitter la console',
        items: [
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Une pastille SSV / TSA sur la ligne de verdict. Elle ouvre les trois documents SharePoint de l’adresse : le SSV, le TSA à signer et le TSA signé.',
            },
            {
                kind: 'fix', area: 'Console DMP',
                text: 'Une seule pastille au lieu de deux, et les trois documents alignés en colonne.',
            },
            {
                kind: 'fix', area: 'Console DMP',
                text: 'Ouverte depuis un ticket, la fenêtre de la pastille s’affichait mais aucun lien n’était cliquable.',
            },
        ],
    },
    {
        version: '1.13.0',
        date: '2026-09-11',
        title: 'Retrouver un champ parmi les 105 colonnes de la console',
        items: [
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Un filtre sur le nom des colonnes : taper une partie d’un nom ne garde que les lignes correspondantes parmi les quelque 105 colonnes de l’adresse.',
            },
            {
                kind: 'fix', area: 'Console DMP',
                text: 'La barre du filtre se voyait à peine : elle est désormais bien visible.',
            },
        ],
    },
    {
        version: '1.12.0',
        date: '2026-09-10',
        title: 'Ne plus ajouter une boîte là où il n’y a plus de place',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Quand la capacité calculée vaut zéro, l’ajout de boîte est refusé, et l’écran dit pourquoi : unités construites, unités déjà ajoutées, plafond de la règle, et que la demande doit passer par Build. Un administrateur peut passer outre, avec un motif obligatoire.',
            },
            {
                kind: 'fix', area: 'Fenêtre de validation',
                text: 'Les adresses dont la capacité n’est PAS calculable ne sont pas bloquées — elles sont 51 sur 216. Les fermer aurait arrêté un quart des dossiers faute de donnée, en confondant « il ne reste rien » et « on ne sait pas ».',
            },
            {
                kind: 'feature', area: 'Journal de validation',
                text: 'Le motif d’un passage outre s’affiche enfin sur la ligne « Boîtes ajoutées », avec le calcul tel qu’il était sous les yeux au moment du geste. Il était enregistré depuis le 4 septembre, et lisible nulle part.',
            },
            {
                kind: 'feature', area: 'Validation d’adresse',
                text: 'Une étoile sur chaque ligne de la file, et une vue « Favoris » dans le menu des vues. Un favori SURVIT au verdict — il ne part qu’au reclic — pour que le dossier dont on veut parler jeudi ne disparaisse pas parce qu’il a été traité mardi. Chacun peut consulter la liste de chacun, filtrable par personne.',
            },
            {
                kind: 'feature', area: 'Notes perso',
                text: 'Un nouvel onglet à deux étages : des réponses types, écrites par les administrateurs et lues par toute l’équipe, et des notes personnelles visibles de leur seul auteur. Cartes, recherche sur le titre ET le contenu, tri, et copie en un clic depuis la carte.',
            },
            {
                kind: 'feature', area: 'Notes perso',
                text: 'Un bouton « Insérer » dans la fenêtre de validation colle une réponse type dans le message à l’opérateur, à la suite de ce qui est déjà écrit. Une réponse déjà insérée est grisée, et un bouton « Vider » remet le champ à zéro.',
            },
            {
                kind: 'feature', area: 'Notes perso',
                text: 'Une note personnelle se rattache à un ticket mis en favori, avec son numéro et son objet. Le lien reste lisible même après le retrait de l’étoile.',
            },
        ],
    },
    {
        version: '1.11.0',
        date: '2026-09-07',
        title: 'Savoir de quelle commune on parle, et passer la main sans perdre le dossier',
        items: [
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'Le code postal et la ville de chaque adresse appariée. Sur une demande qui remonte plusieurs bâtiments, c’est souvent le seul élément qui les distingue — la table des demandes, elle, ne porte aucun code postal.',
            },
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'La zone de déploiement du DMP, distincte de la zone déclarée par le sous-traitant, avec une pastille sur les zones greenfield — celles qu’on ne traite pas.',
            },
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'Un bouton pour passer la main à un collègue, avec un message qui est CONSERVÉ, et un filtre « chez moi ». Une demande MDU n’a ni JVID ni ticket : le transfert vit entièrement dans l’application.',
            },
            {
                kind: 'fix', area: 'Création d’adresse',
                text: 'Le type de logement se dérive de l’adresse, et non du bâtiment. Un même bâtiment peut porter un vrai SDU au n°9 et un MDU au n°13 : basculer tout le bâtiment corrompait le premier. Mesuré : 15 807 bâtiments en scope groupent plusieurs numéros de maison.',
            },
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'Les temps d’attente : revenir à la file après un feedback est instantané au lieu de vingt-cinq secondes, et la fiche d’une demande ne relit plus rien.',
            },
        ],
    },
    {
        version: '1.10.0',
        date: '2026-09-04',
        title: 'Savoir, avant d’ajouter une boîte, s’il reste de la fibre',
        items: [
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Un bloc « Capacité fibre » en tête de la section MDU : combien d’unités sont construites, combien ont déjà été ajoutées, le plafond de la règle, et ce qu’il reste. Le nombre n’est jamais nu — l’arithmétique est dessous, et les réserves sont à l’écran, pas dans une infobulle.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Une pastille par ADRESSE POSTALE, et non par bâtiment : un même bâtiment peut grouper plusieurs numéros, chacun avec son cadran et son plafond. Cliquer la pastille ouvre le détail du calcul.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Au moment d’ajouter une boîte, un avertissement si la capacité ne suffit pas, avec un motif obligatoire enregistré dans le journal. Jamais bloquant à ce stade : les règles n’étaient pas encore validées.',
            },
            {
                kind: 'feature', area: 'Fenêtre de validation',
                text: 'Le cadran vient du site survey quand il existe, sinon du code de livraison — et l’écran dit lequel des deux parle. Les variantes Ax et Cx se traitent comme A et C ; Dx (le QDX) reste sans règle définie, et le dit au lieu d’en inventer une.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Le code de zone lisible (« GF004-01-001 ») est désormais lu, à la place de l’identifiant interne.',
            },
        ],
    },
    {
        version: '1.9.0',
        date: '2026-09-02',
        title: 'Classer les demandes MDU juste, et vérifier une adresse sans quitter le dossier',
        items: [
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'Le classement ne compte plus l’adresse du bâtiment comme un logement, et il tient compte du suffixe du numéro. Sur 97 demandes en cours, 8 changent de cas et 5 quittent « remettre en scope » ; les appariements qui remontaient plusieurs bâtiments passent de 6 à 1.',
            },
            {
                kind: 'fix', area: 'Requêtes MDU',
                text: '« MDU incomplet » devient un seuil et non une différence : une seule unité en scope alors que d’autres existent, plutôt que « au moins une unité hors scope ».',
            },
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'Recherche par JVID, en plus du numéro de demande et du numéro de maison.',
            },
            {
                kind: 'feature', area: 'Requêtes MDU',
                text: 'La file s’affiche instantanément : les demandes en cours sont tenues en mémoire, avec l’heure de la donnée affichée et un bouton « Actualiser ». Toute écriture qui change ce que la file montre la met à jour.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Un aller-retour depuis le dossier : ouvrir la console sur l’adresse du ticket, et revenir là où on en était.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'La carte de l’adresse pour juger sur pièce, et un menu unique à la place de la barre d’outils.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Poser un fake ODF sur une adresse qui n’en a pas.',
            },
            {
                kind: 'fix', area: 'Console DMP',
                text: 'La modale de confirmation perdait le focus à chaque frappe : on ne pouvait tout simplement pas écrire dans le champ.',
            },
            {
                kind: 'fix', area: 'Mise à jour',
                text: 'L’écran pouvait rester sur la version précédente après une mise à jour, indéfiniment, sans rien laisser paraître. La page est désormais revalidée à chaque chargement.',
            },
        ],
    },
    {
        version: '1.8.0',
        date: '2026-09-01',
        title: 'Remettre deux boîtes dans le bon ordre, et le dire à Proximus',
        items: [
            {
                kind: 'feature', area: 'Retour blacklist',
                text: 'Une nouvelle page d’administration : chaque correction d’adresse faite sur un JVID que Proximus a blacklisté y est enregistrée — l’adresse avant, l’adresse après, le motif de la blacklist, qui a corrigé et quand. Les corrections sur des adresses non blacklistées ne sont pas concernées.',
            },
            {
                kind: 'feature', area: 'Retour blacklist',
                text: 'Ces lignes s’écrivent dans le fichier Excel que Proximus consulte sur notre SharePoint. Les colonnes sont repérées par leur nom dans le fichier : ce sont SES en-têtes qui décident, et une colonne qu’on ne reconnaît pas n’est jamais touchée. L’envoi est livré ÉTEINT — l’aperçu montre les cellules exactes avant qu’une seule soit écrite.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Corriger une adresse dont le JVID est sur la blacklist Proximus n’écrit plus dans le référentiel DMP : la correction est enregistrée comme LEUR déclaration d’adresse. L’écran l’annonce en rouge avant la saisie, et le message de confirmation le redit — « j’ai corrigé l’adresse » ne veut pas dire la même chose des deux côtés. Le fichier de retour leur annonce alors notre valeur actuelle et la correction en vis-à-vis.',
            },
            {
                kind: 'feature', area: 'Retour blacklist',
                text: 'Un bouton « Rattraper depuis le journal » reconstruit les lignes manquantes à partir de l’historique des actions. C’est le filet : une correction faite pendant que la blacklist n’était pas à jour se retrouve après coup. À relancer après chaque dépôt d’une nouvelle blacklist.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Un bouton « Échanger deux boîtes » : on saisit le second JVID, l’écran propose l’échange des deux numéros de boîte — adresse par adresse, boîte actuelle puis boîte après échange — et il n’y a plus qu’à valider. Aucun numéro à retaper.',
            },
            {
                kind: 'fix', area: 'Console DMP',
                text: 'Cet échange était jusqu’ici IMPOSSIBLE à faire proprement : corriger la première adresse la rendait, le temps d’une écriture, le sosie de la seconde, et la correction était refusée. Les deux lignes changent désormais ensemble, ou aucune des deux.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'L’écran d’échange avertit en rouge quand les deux adresses ne sont pas dans le même bâtiment, et en ambre quand un client est raccordé à l’une d’elles — sans jamais interdire. Il affiche aussi les numéros que les opérateurs, eux, utilisent pour ces deux logements, sans les modifier.',
            },
        ],
    },
    {
        version: '1.7.0',
        date: '2026-08-31',
        title: 'Savoir pourquoi une adresse n’arrive pas chez le partenaire',
        items: [
            {
                kind: 'feature', area: 'Fiche d’adresse',
                text: 'Un verdict en tête de fiche : « visible chez le partenaire » ou non, et quand ce n’est pas le cas, LA condition qui bloque, nommée. Dix conditions sont vérifiées ; il suffit d’une seule pour que l’adresse disparaisse du service file. Le verdict apparaît dans la console DMP, dans la fenêtre de validation et dans la fiche d’appartement.',
            },
            {
                kind: 'feature', area: 'Fiche d’adresse',
                text: 'L’absence du service file est confrontée à la DATE de l’export chargé : une adresse publiée après l’export ne peut évidemment pas y figurer, et n’est donc plus signalée comme une anomalie. Seul le cas vraiment anormal — tout est en règle, l’adresse est absente, rien n’a bougé depuis — est mis en avant.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Chaque condition bloquante gagne son bouton de réparation : « Publier » pour les drapeaux de publication et le retour en scope, « Déduire du bâtiment » pour les identifiants réseau manquants. La déduction dit d’où vient la valeur — le frère ou le voisin — et nomme le JVID source pour qu’on puisse le vérifier.',
            },
            {
                kind: 'feature', area: 'Console DMP',
                text: 'Une action d’exception « Corriger le statut seul », avec motif obligatoire. Le statut reste normalement déduit des actions (Release, Un-release) ; cette sortie de secours existe pour le seul cas qu’aucune action ne couvre. L’écran avertit avant de valider quand le choix contredit la publication ou désarme les gardes de HOMES_CONNECT.',
            },
            {
                kind: 'feature', area: 'Journal d’actions',
                text: 'Le dépliage montre un tableau de différences au lieu de deux blocs JSON : seuls les champs qui ont bougé, avant → après, rouge pour ce qui disparaît et vert pour ce qui prend sa place. En moyenne 6 lignes au lieu de 105. Les champs inchangés et le JSON brut restent accessibles, repliés.',
            },
            {
                kind: 'feature', area: 'Journal d’actions',
                text: 'Chaque ligne annonce ce qu’elle a fait sans qu’on la déplie — « Statut ESCALATED → VALIDATED », « 5 champs modifiés ». Calculé sur tout l’historique, y compris les actions déjà enregistrées.',
            },
            {
                kind: 'fix', area: 'Journal d’actions',
                text: 'Le mot « unknown » ne s’affiche plus : chaque action porte un libellé. Une case « masquer le bruit », cochée par défaut, écarte les 641 prises de verrou de la fenêtre de validation — un bruit d’historique qui occupait 41 % du journal.',
            },
            {
                kind: 'data', area: 'Données DMP',
                text: '32 adresses publiées mais invisibles au partenaire ont été réparées : leurs identifiants de PoP manquants ont été repris du bâtiment ou du voisin. Les adresses publiées et incomplètes passent de 56 à 24 ; les 24 restantes demandent un arbitrage humain.',
            },
        ],
    },
    {
        version: '1.6.0',
        date: '2026-08-28',
        title: 'La réunion du 27 août, et le français par défaut',
        items: [
            { kind: 'feature', area: 'Application', text: 'L’application s’ouvre en français, quelle que soit la langue du navigateur.' },
            {
                kind: 'feature', area: 'Fiche d’adresse',
                text: 'Le lien Google part désormais des coordonnées du DMP et non plus du libellé de l’adresse — Google ne re-géocode plus pour son compte une adresse qui est justement en litige. Deux boutons distincts : Street View, ouvert directement sur le panorama, et la mesure de distance. Un lien de repli reste offert si le point tombe à côté.',
            },
            { kind: 'feature', area: 'Console DMP', text: 'Un admin peut corriger l’identité d’une adresse (rue, numéro, boîte, code postal…) sans passer par un ticket, avec la même vérification du code postal que partout ailleurs.' },
            { kind: 'feature', area: 'Fiche d’appartement', text: 'Corriger le statut de livraison sans avoir à releaser l’adresse — ce qui rend enfin modifiable le code d’une adresse déjà publiée.' },
            { kind: 'feature', area: 'Fiche d’appartement', text: 'Corriger le type de logement SDU ↔ MDU dans le DMP, une ligne à la fois. C’est la colonne que l’opérateur désigne quand il demande « passez l’adresse en MDU » ; jusqu’ici on ne corrigeait que la déclaration opérateur.' },
            { kind: 'feature', area: 'Fenêtre de validation', text: 'Une garde avant de rejeter : elle propose de poser la question à l’opérateur plutôt que de rejeter. Un rejet clôture désormais le ticket.' },
            { kind: 'feature', area: 'Fiche d’adresse', text: 'Le décodeur des codes de livraison couvre le schéma 11xx, et une adresse non desservie dit pourquoi elle ne l’est pas.' },
            { kind: 'fix', area: 'Fenêtre de validation', text: 'Un message envoyé ne reste plus affiché dans la zone de saisie : ce qui est parti vit dans le fil du ticket, le composeur revient vide.' },
            { kind: 'fix', area: 'Fenêtre de validation', text: 'Passer au ticket suivant remet à zéro tout ce qui appartenait au précédent — la case « Interne », les mentions, le message, la pièce jointe sélectionnée. Une question publique ne peut plus partir en note interne par inadvertance.' },
            { kind: 'fix', area: 'Console DMP', text: 'L’autocomplétion d’adresses de Chrome ne vient plus polluer les champs.' },
        ],
    },
    {
        version: '1.5.0',
        date: '2026-08-17',
        title: 'Connexion au DMP par le pooler partagé',
        items: [
            { kind: 'fix', area: 'Infrastructure', text: 'La connexion au DMP passe par le pooler partagé, ce qui stabilise les temps de réponse.' },
            { kind: 'fix', area: 'Synchronisation', text: 'Les tickets inchangés sont ignorés à la synchronisation — moins de sollicitation du DMP, synchronisation plus rapide.' },
        ],
    },
    {
        version: '1.4.0',
        date: '2026-08-13',
        title: 'La file MDU utilisable, et le journal navigable',
        items: [
            { kind: 'fix', area: 'MDU absents d’Appeee', text: 'La file ne pouvait pas s’afficher en production : elle mettait une minute et se faisait couper. Elle répond maintenant en quelques secondes.' },
            { kind: 'feature', area: 'MDU absents d’Appeee', text: 'La file ne montre plus que les demandes EN COURS, et ses colonnes se trient — à commencer par le type de cas.' },
            { kind: 'feature', area: 'Journal d’actions', text: 'Pagination au-delà de 200 lignes, retour vers le ticket d’un clic, et les créations d’adresse deviennent lisibles.' },
            { kind: 'feature', area: 'Fiche d’appartement', text: 'Retirer une adresse de la Scope List V15 ou V2 depuis le volet.' },
            { kind: 'fix', area: 'Fenêtre de validation', text: '« Ticket suivant » suit la file telle qu’elle est filtrée et triée à l’écran, et le tri est mémorisé.' },
            { kind: 'feature', area: 'Application', text: 'Le logo Address Validation et une icône d’application.' },
        ],
    },
    {
        version: '1.3.0',
        date: '2026-08-11',
        title: 'La console DMP répond avant qu’on la déroule',
        items: [
            { kind: 'feature', area: 'Console DMP', text: 'Un verdict en pastilles au-dessus de la fiche : présence dans le service file, dans le deployment list, dans les PST Build. Le détail s’ouvre en cliquant la pastille, plutôt que d’épingler trois tableaux sous chaque recherche.' },
            { kind: 'feature', area: 'Console DMP', text: 'Un troisième fichier de référence, la blacklist — pastille rouge pleine qui annonce son motif, parce qu’y figurer n’est pas une bonne nouvelle.' },
            { kind: 'feature', area: 'Console DMP', text: 'Créer une adresse ou ajouter une boîte depuis la console, admins seuls.' },
            { kind: 'feature', area: 'Release', text: 'Le release refuse une adresse à qui il manque un champ exigé par le service file, en nommant le champ : elle serait marquée publiée et n’arriverait jamais chez le partenaire.' },
            { kind: 'fix', area: 'Fenêtre de validation', text: 'La bascule automatique en MDU ne compte que les logements EN SCOPE — compter toutes les unités annonçait MDU à l’opérateur pour des bâtiments où un seul logement est déployé.' },
        ],
    },
    {
        version: '1.2.0',
        date: '2026-08-07',
        title: 'Le rôle Build et « le dossier est chez X »',
        items: [
            { kind: 'feature', area: 'Application', text: 'Un rôle « Build » avec la console DMP comme écran unique, et l’entrée « Console DMP » dans le menu des admins.' },
            { kind: 'feature', area: 'Fenêtre de validation', text: '« Le dossier est chez X » : un dossier pris en charge remonte en tête de VOTRE file, avec un filtre « à moi ou à personne ».' },
            { kind: 'feature', area: 'Journal d’actions', text: 'Tri et filtre par action.' },
            { kind: 'feature', area: 'Fenêtre de validation', text: 'Un bouton d’upload sur la carte « Pièces jointes ».' },
        ],
    },
    {
        version: '1.1.0',
        date: '2026-08-06',
        title: 'Les réunions du 4 et du 6 août',
        items: [
            { kind: 'feature', area: 'Fenêtre de validation', text: 'Escalade à deux niveaux, question publique dans « Send to Build », réponses badgées par rôle, et lien « Ticket précédent ».' },
            { kind: 'feature', area: 'Fenêtre de validation', text: 'La vue de la file est respectée d’un ticket à l’autre, le tri de « À traiter » est conservé, et une erreur d’enregistrement devient visible.' },
            { kind: 'feature', area: 'Adresse', text: 'La distance depuis la rue est réservée aux SDU, et la suggestion de numéro de boîte tient compte des renommages opérateur.' },
            { kind: 'feature', area: 'Administration', text: 'Tri par colonne et filtres par champ sur la table de mapping et le registre JVID.' },
            { kind: 'fix', area: 'Fenêtre de validation', text: 'La pièce jointe suit l’action réellement choisie, et plus seulement « Enregistrer ».' },
            { kind: 'fix', area: 'Fenêtre de validation', text: 'Le verdict n’écrase plus la table de mapping avec le pré-remplissage du panneau.' },
        ],
    },
    {
        version: '1.0.0',
        date: '2026-07-09',
        title: 'Mise en service',
        items: [
            { kind: 'feature', area: 'Application', text: 'Première mise en production : file de validation, fenêtre de validation à trois panneaux, console DMP en lecture, connexion au DMP et à Uniticket.' },
        ],
    },
];
/** Le nombre total d'entrées — utilisé par l'écran pour annoncer ce qu'il montre. */
export const CHANGELOG_RELEASES = CHANGELOG.length;
//# sourceMappingURL=changelog.js.map